import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as clientPOCreationAPI from '../../api/AccountsAPI/clientPOCreationAPI';

// Async Thunks
// =============================================

export const fetchAllCostCentersClient = createAsyncThunk(
    'clientPOCreation/fetchAllCostCentersClient',
    async (_, { rejectWithValue }) => {
        try {
            const response = await clientPOCreationAPI.getAllCostCentersClient();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers');
        }
    }
);

export const fetchAllClients = createAsyncThunk(
    'clientPOCreation/fetchAllClients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await clientPOCreationAPI.getAllClients();
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Clients');
        }
    }
);

export const fetchSubClientsByClientID = createAsyncThunk(
    'clientPOCreation/fetchSubClientsByClientID',
    async (clientcode, { rejectWithValue }) => {
        try {
            const response = await clientPOCreationAPI.getSubClientsByClientID(clientcode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Sub-Clients');
        }
    }
);

export const fetchCCDetailsByCcCode = createAsyncThunk(
    'clientPOCreation/fetchCCDetailsByCcCode',
    async (ccCode, { rejectWithValue }) => {
        try {
            const response = await clientPOCreationAPI.getCCDetailsByCcCode(ccCode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Center Details');
        }
    }
);

export const saveNewClientPO = createAsyncThunk(
    'clientPOCreation/saveNewClientPO',
    async (params, { rejectWithValue }) => {
        try {
            const response = await clientPOCreationAPI.addNewClientPO(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Client PO');
        }
    }
);

// Initial State
// =============
const initialState = {
    costCenters: [],
    clients: [],
    subClients: [],
    ccDetails: null,
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        costCenters: false,
        clients: false,
        subClients: false,
        ccDetails: false,
        save: false,
    },

    errors: {
        costCenters: null,
        clients: null,
        subClients: null,
        ccDetails: null,
        save: null,
    },
};

const clientPOCreationSlice = createSlice({
    name: 'clientPOCreation',
    initialState,
    reducers: {
        clearSubClients: (state) => {
            state.subClients = [];
            state.errors.subClients = null;
        },
        clearCCDetails: (state) => {
            state.ccDetails = null;
            state.errors.ccDetails = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetClientPOCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllCostCentersClient.pending, (state) => {
                state.loading.costCenters = true;
                state.errors.costCenters = null;
            })
            .addCase(fetchAllCostCentersClient.fulfilled, (state, action) => {
                state.loading.costCenters = false;
                state.costCenters = action.payload?.Data || [];
            })
            .addCase(fetchAllCostCentersClient.rejected, (state, action) => {
                state.loading.costCenters = false;
                state.errors.costCenters = action.payload;
                state.costCenters = [];
            });

        builder
            .addCase(fetchAllClients.pending, (state) => {
                state.loading.clients = true;
                state.errors.clients = null;
            })
            .addCase(fetchAllClients.fulfilled, (state, action) => {
                state.loading.clients = false;
                state.clients = action.payload?.Data || [];
            })
            .addCase(fetchAllClients.rejected, (state, action) => {
                state.loading.clients = false;
                state.errors.clients = action.payload;
                state.clients = [];
            });

        builder
            .addCase(fetchSubClientsByClientID.pending, (state) => {
                state.loading.subClients = true;
                state.errors.subClients = null;
            })
            .addCase(fetchSubClientsByClientID.fulfilled, (state, action) => {
                state.loading.subClients = false;
                state.subClients = action.payload?.Data || [];
            })
            .addCase(fetchSubClientsByClientID.rejected, (state, action) => {
                state.loading.subClients = false;
                state.errors.subClients = action.payload;
                state.subClients = [];
            });

        builder
            .addCase(fetchCCDetailsByCcCode.pending, (state) => {
                state.loading.ccDetails = true;
                state.errors.ccDetails = null;
            })
            .addCase(fetchCCDetailsByCcCode.fulfilled, (state, action) => {
                state.loading.ccDetails = false;
                const data = action.payload?.Data;
                state.ccDetails = Array.isArray(data) ? (data[0] || null) : (data || null);
            })
            .addCase(fetchCCDetailsByCcCode.rejected, (state, action) => {
                state.loading.ccDetails = false;
                state.errors.ccDetails = action.payload;
                state.ccDetails = null;
            });

        builder
            .addCase(saveNewClientPO.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveNewClientPO.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string'
                    ? action.payload.Data
                    : (typeof action.payload === 'string' ? action.payload : '');

                if (responseStr === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveNewClientPO.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearSubClients,
    clearCCDetails,
    clearSaveResult,
    resetClientPOCreation,
} = clientPOCreationSlice.actions;

// Selectors
// =========
export const selectCostCentersArray = (state) => {
    const cc = state.clientPOCreation.costCenters;
    return Array.isArray(cc) ? cc : [];
};
export const selectCostCentersLoading = (state) => state.clientPOCreation.loading.costCenters;

export const selectClientsArray = (state) => {
    const c = state.clientPOCreation.clients;
    return Array.isArray(c) ? c : [];
};
export const selectClientsLoading = (state) => state.clientPOCreation.loading.clients;

export const selectSubClientsArray = (state) => {
    const sc = state.clientPOCreation.subClients;
    return Array.isArray(sc) ? sc : [];
};
export const selectSubClientsLoading = (state) => state.clientPOCreation.loading.subClients;

export const selectCCDetails = (state) => state.clientPOCreation.ccDetails;
export const selectCCDetailsLoading = (state) => state.clientPOCreation.loading.ccDetails;

export const selectSaveStatus = (state) => state.clientPOCreation.saveStatus;
export const selectSaveLoading = (state) => state.clientPOCreation.loading.save;
export const selectSaveError = (state) => state.clientPOCreation.errors.save;

export default clientPOCreationSlice.reducer;
